import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Youtube, Twitter, MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { useSiteSettings, waLink, telLink } from '../lib/SiteSettingsContext';
import { BRAND_LOGO_URL, BRAND_NAME } from '../lib/brand';
import FooterSeoLinksSection from './FooterSeoLinksSection';

export default function Footer() {
    const { settings: s } = useSiteSettings();
    const brand = s.site_name || BRAND_NAME;
    const logoUrl = s.site_logo_url || BRAND_LOGO_URL;

    const socials = [
        [Facebook, s.facebook_url, 'Facebook'],
        [Instagram, s.instagram_url, 'Instagram'],
        [Linkedin, s.linkedin_url, 'LinkedIn'],
        [Youtube, s.youtube_url, 'YouTube'],
        [Twitter, s.twitter_url, 'Twitter'],
    ].filter(([, url]) => url && url !== '#');

    return (
        <>
        <FooterSeoLinksSection />
        <footer className="bg-[#1B4332] text-white pt-10 sm:pt-12 pb-8" data-testid="footer">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
                    {/* Brand */}
                    <div>
                        <img
                            src={logoUrl}
                            alt={brand}
                            className="h-20 w-auto rounded-md object-contain mb-4"
                            data-testid="footer-logo"
                        />
                        <p className="text-white/65 text-[14px] leading-relaxed mb-5">
                            {s.site_tagline}
                        </p>

                        <div className="bg-white/[0.06] rounded-xl p-4 mb-5">
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#E07B39] mb-2">RERA Registered Portal</div>
                            <div className="text-white/65 text-[12px]">All listings verified against state RERA portals.</div>
                        </div>

                        <div className="mb-4">
                            <div className="text-[13px] font-semibold mb-2">Get Property Updates</div>
                            <div className="text-white/65 text-[11px] mb-3">New launches, price drops, market news</div>
                            <div className="flex gap-2">
                                <label htmlFor="footer-subscribe-email" className="sr-only">Email address for property updates</label>
                                <input
                                    id="footer-subscribe-email"
                                    type="email"
                                    placeholder="Your email"
                                    className="flex-1 px-3 py-2 rounded-lg bg-white/[0.08] border border-white/[0.15] text-white placeholder:text-white/55 text-[13px]"
                                    data-testid="footer-subscribe-email"
                                    aria-label="Email address for property updates"
                                />
                                <button className="btn-saffron px-4 text-[13px]" data-testid="footer-subscribe-btn" aria-label="Subscribe to property updates">Join</button>
                            </div>
                        </div>

                        {socials.length > 0 && (
                            <div className="flex gap-2">
                                {socials.map(([Icon, url, label], i) => (
                                    <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="w-10 h-10 rounded-full bg-white/[0.10] flex items-center justify-center hover:bg-[#E07B39] transition-colors"
                                        aria-label={`${brand} on ${label}`}
                                    >
                                        <Icon size={16} aria-hidden="true" />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-[13px] font-semibold mb-4 tracking-wide">Quick Links</div>
                        <ul className="space-y-2.5">
                            {[
                                ['Buy Property', '/buy'], ['Rent Property', '/rent'], ['New Projects', '/new-projects'],
                                ['PG / Co-living', '/pg'], ['Plots & Land', '/plots'], ['Resale', '/resale'], ['Commercial', '/commercial'],
                            ].map(([label, to]) => (
                                <li key={to}><Link to={to} className="text-white/65 hover:text-white text-[14px] transition-colors">{label}</Link></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-[13px] font-semibold mb-4 tracking-wide">Top Cities</div>
                        <ul className="space-y-2.5">
                            {[
                                ['Mumbai', 'mumbai'], ['Bengaluru', 'bengaluru'], ['Delhi NCR', 'delhi-ncr'], ['Gurgaon', 'gurgaon'],
                                ['Noida', 'noida'], ['Pune', 'pune'], ['Hyderabad', 'hyderabad'], ['Chennai', 'chennai'],
                            ].map(([label, slug]) => (
                                <li key={slug}><Link to={`/city/${slug}`} className="text-white/65 hover:text-white text-[14px] transition-colors">{label}</Link></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-[13px] font-semibold mb-4 tracking-wide">Resources</div>
                        <ul className="space-y-2.5 mb-6">
                            <li><Link to="/blog" className="text-white/65 hover:text-white text-[14px]">Blog</Link></li>
                            <li><Link to="/tools/emi-calculator" className="text-white/65 hover:text-white text-[14px]">EMI Calculator</Link></li>
                            <li><Link to="/tools/stamp-duty" className="text-white/65 hover:text-white text-[14px]">Stamp Duty Calculator</Link></li>
                            <li><Link to="/tools/vastu-checker" className="text-white/65 hover:text-white text-[14px]">Vastu Checker</Link></li>
                            <li><Link to="/tools/area-converter" className="text-white/65 hover:text-white text-[14px]">Area Converter</Link></li>
                            <li><Link to="/about-us" className="text-white/65 hover:text-white text-[14px]">About Us</Link></li>
                            <li><Link to="/contact" className="text-white/65 hover:text-white text-[14px]">Contact</Link></li>
                        </ul>

                        <div className="space-y-2 text-[13px] text-white/70">
                            <a href={telLink(s.contact_phone)} className="flex items-center gap-2 hover:text-white"><Phone size={14} className="text-[#E07B39]" /> {s.contact_phone}</a>
                            <a href={waLink(s.contact_whatsapp)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><MessageCircle size={14} className="text-[#E07B39]" /> {s.contact_whatsapp} (WhatsApp)</a>
                            <a href={`mailto:${s.contact_email}`} className="flex items-center gap-2 hover:text-white"><Mail size={14} className="text-[#E07B39]" /> {s.contact_email}</a>
                            <div className="flex items-start gap-2"><MapPin size={14} className="text-[#E07B39] mt-0.5" /> {s.contact_address}</div>
                        </div>
                    </div>
                </div>

                <div className="border-t border-white/10 mt-12 pt-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-white/55">
                        <div>© {new Date().getFullYear()} {brand}. All Rights Reserved.</div>
                        <div className="text-[11px]">CIN: {s.cin} {s.rera_agent_reg ? `| RERA Agent: ${s.rera_agent_reg}` : ''}</div>
                        <div className="flex gap-4">
                            <Link to="/privacy" className="hover:text-white">Privacy</Link>
                            <Link to="/terms" className="hover:text-white">Terms</Link>
                            <Link to="/disclaimer" className="hover:text-white">Disclaimer</Link>
                            <Link to="/sitemap" className="hover:text-white">Sitemap</Link>
                        </div>
                    </div>
                    <p className="text-center text-[11px] text-white/40 mt-4 max-w-3xl mx-auto">
                        This portal is for informational purposes only. Prices mentioned are indicative.
                        Please verify RERA registration of projects before booking.
                    </p>
                </div>
            </div>
        </footer>
        </>
    );
}
